// super-excalidraw: Presentation sidebar tab. Lists upstream Frame elements
// as slides with thumbnails + editable titles + drag-to-reorder; "Create
// slide" wraps the current viewport in a new frame; "Start presentation"
// hands off to PresentationPlayer for full-screen step-through.

import React, { useEffect, useMemo, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import "./PresentationTab.scss";

type FrameLite = {
  id: string;
  name: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PresentationTabProps = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onStart: (orderedFrameIds: string[]) => void;
  /** Whether the parent sidebar tab is currently visible — used to refresh
   *  the frame list + thumbnails when the tab re-opens. */
  visible?: boolean;
};

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const renderFrameThumbnail = async (
  frame: FrameLite,
  allElements: readonly any[],
  files: any,
  appState: any,
): Promise<string> => {
  try {
    const children = allElements.filter((e: any) => e.frameId === frame.id);
    if (children.length === 0) {
      return "";
    }
    const { exportToBlob } = await import("@excalidraw/utils/export");
    const blob = await exportToBlob({
      elements: [...children, frame as any],
      appState: { ...appState, exportBackground: true },
      files,
      mimeType: "image/png",
      maxWidthOrHeight: 320,
      exportPadding: 8,
    } as any);
    if (!blob) {
      return "";
    }
    return await blobToDataURL(blob);
  } catch (e) {
    return "";
  }
};

// Read the saved order from the diagram's appState.customData. If absent,
// fall back to the order frames appear in the elements array.
const readSavedOrder = (api: ExcalidrawImperativeAPI | null): string[] => {
  if (!api) {
    return [];
  }
  const order =
    ((api.getAppState() as any).customData?.presentationOrder as
      | string[]
      | undefined) ?? [];
  return Array.isArray(order) ? order : [];
};

const persistOrder = (
  api: ExcalidrawImperativeAPI | null,
  order: string[],
) => {
  if (!api) {
    return;
  }
  const cur = (api.getAppState() as any).customData ?? {};
  api.updateScene({
    appState: {
      ...api.getAppState(),
      customData: { ...cur, presentationOrder: order },
    } as any,
  });
};

export const PresentationTab: React.FC<PresentationTabProps> = ({
  excalidrawAPI,
  onStart,
  visible,
}) => {
  const [frames, setFrames] = useState<FrameLite[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(
    null,
  );
  const tickRef = useRef(0);

  const refresh = async () => {
    if (!excalidrawAPI) {
      return;
    }
    const elements = excalidrawAPI.getSceneElements();
    const allFrames: FrameLite[] = elements
      .filter((e: any) => e.type === "frame" && !e.isDeleted)
      .map((e: any) => ({
        id: e.id,
        name: e.name ?? null,
        x: e.x,
        y: e.y,
        width: e.width,
        height: e.height,
      }));

    const order = readSavedOrder(excalidrawAPI);
    const orderedIds = order.length
      ? [
          ...order.filter((id) => allFrames.some((f) => f.id === id)),
          ...allFrames
            .filter((f) => !order.includes(f.id))
            .map((f) => f.id),
        ]
      : allFrames.map((f) => f.id);
    const ordered = orderedIds
      .map((id) => allFrames.find((f) => f.id === id))
      .filter(Boolean) as FrameLite[];

    setFrames(ordered);

    // Refresh thumbnails (best-effort, async).
    const myTick = ++tickRef.current;
    const allElements = elements as readonly any[];
    const files = excalidrawAPI.getFiles();
    const appState = excalidrawAPI.getAppState();
    const next: Record<string, string> = {};
    for (const f of ordered) {
      const url = await renderFrameThumbnail(f, allElements, files, appState);
      if (myTick !== tickRef.current) {
        return;
      }
      if (url) {
        next[f.id] = url;
      }
    }
    setThumbs(next);
  };

  useEffect(() => {
    refresh();
    if (!excalidrawAPI) {
      return;
    }
    const off = excalidrawAPI.onChange(() => {
      // light debounce — refresh metadata fast, thumbnails on the trailing edge
      window.requestAnimationFrame(refresh);
    });
    return () => off();
  }, [excalidrawAPI]);

  useEffect(() => {
    if (visible) {
      refresh();
    }
  }, [visible]);

  const onCreateSlide = () => {
    if (!excalidrawAPI) {
      return;
    }
    const appState = excalidrawAPI.getAppState();
    const w = 960;
    const h = 540;
    const cx = -appState.scrollX + appState.width / 2;
    const cy = -appState.scrollY + appState.height / 2;
    const id = `frame-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
    const newFrame = {
      id,
      type: "frame",
      x: cx - w / 2,
      y: cy - h / 2,
      width: w,
      height: h,
      angle: 0,
      strokeColor: "#a8a8a8",
      backgroundColor: "transparent",
      fillStyle: "solid",
      strokeWidth: 2,
      strokeStyle: "solid",
      roughness: 0,
      opacity: 100,
      groupIds: [],
      frameId: null,
      seed: Math.floor(Math.random() * 2 ** 31),
      version: 1,
      versionNonce: Math.floor(Math.random() * 2 ** 31),
      isDeleted: false,
      boundElements: null,
      updated: Date.now(),
      link: null,
      locked: false,
      name: `Slide ${frames.length + 1}`,
      roundness: null,
    };
    excalidrawAPI.updateScene({
      elements: [
        ...excalidrawAPI.getSceneElementsIncludingDeleted(),
        newFrame as any,
      ],
    });
    refresh();
  };

  const onRename = (frame: FrameLite, newName: string) => {
    if (!excalidrawAPI) {
      return;
    }
    const all = excalidrawAPI.getSceneElementsIncludingDeleted();
    excalidrawAPI.updateScene({
      elements: all.map((e: any) =>
        e.id === frame.id ? { ...e, name: newName, updated: Date.now() } : e,
      ),
    });
  };

  const onDeleteSlide = (frame: FrameLite) => {
    if (!excalidrawAPI) {
      return;
    }
    if (
      !confirm(
        `Delete "${frame.name || "this slide"}"? Children of the frame stay on the canvas.`,
      )
    ) {
      return;
    }
    const all = excalidrawAPI.getSceneElementsIncludingDeleted();
    excalidrawAPI.updateScene({
      elements: all.map((e: any) =>
        e.id === frame.id ? { ...e, isDeleted: true } : e,
      ),
    });
    setFrames((arr) => arr.filter((f) => f.id !== frame.id));
  };

  const onDragStart = (id: string) => setDraggingId(id);
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const onDrop = (targetId: string) => {
    if (!draggingId || draggingId === targetId) {
      setDraggingId(null);
      return;
    }
    const cur = frames.map((f) => f.id);
    const fromIdx = cur.indexOf(draggingId);
    const toIdx = cur.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) {
      return;
    }
    const next = cur.slice();
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggingId);
    setFrames(next.map((id) => frames.find((f) => f.id === id)!));
    persistOrder(excalidrawAPI, next);
    setDraggingId(null);
  };

  const onClickThumb = (frame: FrameLite) => {
    if (!excalidrawAPI) {
      return;
    }
    excalidrawAPI.scrollToContent(frame as any, {
      fitToViewport: true,
      animate: true,
      duration: 300,
    } as any);
  };

  const orderedIds = useMemo(() => frames.map((f) => f.id), [frames]);

  return (
    <div className="presentation-tab">
      <div className="presentation-tab__header">
        <span className="presentation-tab__title">Presentation</span>
        <button
          type="button"
          className="presentation-tab__create"
          onClick={onCreateSlide}
          title="Drop a frame around the current viewport"
        >
          + Create slide
        </button>
      </div>

      {frames.length === 0 ? (
        <div className="presentation-tab__empty">
          <p>No slides yet.</p>
          <p>
            Use the Frame tool from the toolbar (or click <em>Create slide</em>)
            to mark a region of your canvas as a slide.
          </p>
        </div>
      ) : (
        <ul className="presentation-tab__list">
          {frames.map((f, i) => (
            <li
              key={f.id}
              className={
                "presentation-tab__row" +
                (draggingId === f.id ? " is-dragging" : "")
              }
              draggable
              onDragStart={() => onDragStart(f.id)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(f.id)}
            >
              <div
                className="presentation-tab__thumb"
                role="button"
                title="Jump to this slide on the canvas"
                onClick={() => onClickThumb(f)}
              >
                {thumbs[f.id] ? (
                  <img src={thumbs[f.id]} alt={f.name || `Slide ${i + 1}`} />
                ) : (
                  <span className="presentation-tab__thumb-placeholder">
                    {i + 1}
                  </span>
                )}
              </div>
              <div className="presentation-tab__meta">
                {renaming?.id === f.id ? (
                  <input
                    autoFocus
                    className="presentation-tab__rename"
                    value={renaming.value}
                    onChange={(e) =>
                      setRenaming({ id: f.id, value: e.target.value })
                    }
                    onBlur={() => {
                      onRename(f, renaming.value || `Slide ${i + 1}`);
                      setRenaming(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                      if (e.key === "Escape") {
                        setRenaming(null);
                      }
                    }}
                  />
                ) : (
                  <span
                    className="presentation-tab__name"
                    onClick={() =>
                      setRenaming({
                        id: f.id,
                        value: f.name || `Slide ${i + 1}`,
                      })
                    }
                    title="Click to rename"
                  >
                    {f.name || `Slide ${i + 1}`}
                  </span>
                )}
                <button
                  type="button"
                  className="presentation-tab__delete"
                  title="Remove from presentation (frame moves to trash)"
                  onClick={() => onDeleteSlide(f)}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {frames.length > 0 && (
        <div className="presentation-tab__footer">
          <button
            type="button"
            className="presentation-tab__start"
            onClick={() => onStart(orderedIds)}
          >
            ▶ Start presentation
          </button>
        </div>
      )}
    </div>
  );
};
